import {useRef, useState, useMemo} from 'react'
import {
    useFloating,
    useClick,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    offset,
    flip,
    shift,
    arrow,
    autoUpdate,
} from '@floating-ui/react'

const normalizeTrigger = (trigger) => {
    if (Array.isArray(trigger)) return trigger
    return [trigger]
}

export const usePopover = ({
    isOpen: controlledOpen,
    defaultOpen = false,
    onOpenChange,
    placement = 'bottom',
    offset: offsetVal = 7,
    showArrow = false,
    trigger = 'click',
    shouldCloseOnBlur = true,
    shouldCloseOnScroll = false,
    shouldFlip = true,
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
    const arrowRef = useRef(null)

    const isControlled = controlledOpen !== undefined
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen

    const setIsOpen = (nextOpen) => {
        if (!isControlled) setUncontrolledOpen(nextOpen)
        onOpenChange?.(nextOpen)
    }

    const middleware = useMemo(() => {
        const list = [offset(offsetVal), shift({padding: 8})]
        if (shouldFlip) list.push(flip())
        if (showArrow) list.push(arrow({element: arrowRef}))
        return list
    }, [offsetVal, shouldFlip, showArrow])

    const {refs, floatingStyles, context, placement: resolvedPlacement, middlewareData, isPositioned} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware,
    })

    const triggers = normalizeTrigger(trigger)

    const clickInteraction = useClick(context, {enabled: triggers.includes('click')})
    const hoverInteraction = useHover(context, {
        enabled: triggers.includes('hover'),
        move: false,
        delay: {open: 100, close: 150},
    })
    const focusInteraction = useFocus(context, {enabled: triggers.includes('focus')})
    const dismissInteraction = useDismiss(context, {
        outsidePress: true,
        escapeKey: true,
        ancestorScroll: shouldCloseOnScroll,
    })
    const roleInteraction = useRole(context, {role: 'dialog'})

    const {getReferenceProps, getFloatingProps} = useInteractions([
        clickInteraction,
        hoverInteraction,
        focusInteraction,
        dismissInteraction,
        roleInteraction,
    ])

    return {
        isOpen,
        setIsOpen,
        refs,
        floatingStyles,
        isPositioned,
        context,
        resolvedPlacement,
        arrowData: middlewareData.arrow,
        arrowRef,
        getReferenceProps,
        getFloatingProps,
    }
}
